/**
 * Hook to handle duplicate chat data from URL parameters
 * Fetches the original chat data and prepares initial form values based on duplication option
 */

import {
  CreateChatFormData,
  CreateChatType,
  CreatedContext,
  OrderPreferenceType,
} from '@choco-admin/types';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useGetChatData } from './useGetChatData';

export interface UseDuplicateChatDataResponse {
  duplicateData: Partial<CreateChatFormData> | null;
  isLoading: boolean;
  error: Error | null;
}

export const useDuplicateChatData = (): UseDuplicateChatDataResponse => {
  const [searchParams] = useSearchParams();
  const [duplicateData, setDuplicateData] =
    useState<Partial<CreateChatFormData> | null>(null);

  const duplicateFromChatId = searchParams.get('duplicateFrom');
  const duplicateOption = searchParams.get('duplicateOption') as
    | 'buyer'
    | 'supplier'
    | null;
  const duplicateOrderGuide =
    searchParams.get('duplicateOrderGuide') === 'true';

  const { chatData, isLoading, error } = useGetChatData(
    duplicateFromChatId || ''
  );

  useEffect(() => {
    // If there's no duplicate request, set to empty object (not null)
    if (!duplicateFromChatId || !duplicateOption) {
      setDuplicateData({});
      return;
    }

    // If we're waiting for chat data, don't set anything yet
    if (!chatData && isLoading) {
      return;
    }

    // If we couldn't load the chat data, set to empty object
    if (!chatData) {
      setDuplicateData({});
      return;
    }

    let initialData: Partial<CreateChatFormData> = {
      chatType: { value: CreateChatType.ORDERING, label: 'Ordering' },
      createdContext: { value: CreatedContext.ADMIN, label: 'Admin Created' },
    };

    if (duplicateOption === 'buyer') {
      // Same buyer, different supplier
      initialData = {
        ...initialData,
        buyer: chatData.buyerEntity
          ? {
              value: chatData.buyerEntity.id,
              label: chatData.buyerEntity.name,
              metaData: {
                fullAddress: chatData.address || '',
                name: chatData.buyerEntity.name,
                referenceId: chatData.buyerEntity.referenceId || '',
              },
            }
          : null,
        buyerUsers: chatData.users
          .filter((user) => !user.supplier)
          .map((user) => ({
            value: user.id,
            label: user.name,
            metaData: {
              email: user.email || '',
              phone: user.phone || '',
              orderPreferences: user.orderPreferences
                ? [
                    ...(user.orderPreferences.whatsApp
                      ? [
                          {
                            type: OrderPreferenceType.WHATSAPP,
                            value: 'whatsapp',
                          },
                        ]
                      : []),
                    ...(user.orderPreferences.sms
                      ? [{ type: OrderPreferenceType.SMS, value: 'sms' }]
                      : []),
                    ...(user.orderPreferences.fax
                      ? [{ type: OrderPreferenceType.FAX, value: 'fax' }]
                      : []),
                  ]
                : [],
              supplier: false,
              name: user.name,
              referenceId: user.id,
            },
          })),
        deliveryAddress: chatData.address || '',
        additionalEmails: chatData.additionalEmails?.join(', ') || '',
      };
    } else if (duplicateOption === 'supplier') {
      // Same supplier, different buyer
      initialData = {
        ...initialData,
        supplier: chatData.supplierEntity
          ? {
              value: chatData.supplierEntity.id,
              label: chatData.supplierEntity.name,
              metaData: {
                name: chatData.supplierEntity.name,
                supplierId: chatData.supplierEntity.id,
                phone: '',
                email: '',
                orderPreferences: [],
                supplier: true,
                referenceId: chatData.supplierEntity.referenceId || '',
              },
            }
          : null,
        supplierUsers: chatData.users
          .filter((user) => user.supplier)
          .map((user) => ({
            value: user.id,
            label: user.name,
            metaData: {
              email: user.email || '',
              phone: user.phone || '',
              orderPreferences: user.orderPreferences
                ? [
                    ...(user.orderPreferences.whatsApp
                      ? [
                          {
                            type: OrderPreferenceType.WHATSAPP,
                            value: 'whatsapp',
                          },
                        ]
                      : []),
                    ...(user.orderPreferences.sms
                      ? [{ type: OrderPreferenceType.SMS, value: 'sms' }]
                      : []),
                    ...(user.orderPreferences.fax
                      ? [{ type: OrderPreferenceType.FAX, value: 'fax' }]
                      : []),
                  ]
                : [],
              supplier: true,
              name: user.name,
              supplierId: chatData.supplierEntity?.id || '',
              referenceId: user.id,
            },
          })),
        chatName: chatData.chatName || '',
      };

      // Add parent chat info for order guide duplication
      if (duplicateOrderGuide) {
        // Note: parentChatId might not be part of CreateChatFormData
        // This would need to be handled in the creation logic
        (initialData as any).parentChatId = duplicateFromChatId;
      }
    }

    setDuplicateData(initialData);
  }, [chatData, duplicateOption, duplicateFromChatId, duplicateOrderGuide]);

  return {
    duplicateData,
    isLoading: isLoading && !!duplicateFromChatId,
    error: duplicateFromChatId ? error : null,
  };
};
